import { useEffect, useRef, useState } from 'react';
import { partyApi } from '../services/partyApi';

interface AddPhoneInlineProps {
  partyId: string;
  hasPhone: boolean;
  onSaved: () => void | Promise<void>;
}

// Per-row '+ phone' affordance. Click to expand a single-input field;
// press enter to save. Inline (no modal) — the whole flow is "10 digits
// and done."
export function AddPhoneInline({ partyId, hasPhone, onSaved }: AddPhoneInlineProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  async function save() {
    setErr(null);
    if (!value.trim()) { setErr('phone required'); return; }
    setSaving(true);
    try {
      await partyApi.addPhone(partyId, value.trim());
      setOpen(false);
      setValue('');
      await onSaved();
    } catch (e: any) {
      setErr(e?.message ?? 'save failed');
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        title={hasPhone ? 'Add another phone (will replace current)' : 'Add phone (manual from TruePeopleSearch lookup)'}
        className={
          'inline-flex items-center justify-center w-6 h-6 rounded text-[12px] font-mono ' +
          (hasPhone
            ? 'border border-green/40 text-green hover:bg-green/10'
            : 'border border-border text-text-faint hover:text-text hover:border-accent/60')
        }>
        {hasPhone ? '☎' : '+☎'}
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      <input ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') { setOpen(false); setValue(''); setErr(null); }
        }}
        placeholder="303-555-1234"
        disabled={saving}
        className="w-32 bg-bg-input border border-border px-2 py-0.5 rounded text-[12px] font-mono outline-none focus:border-accent" />
      <button onClick={save} disabled={saving}
        className="px-2 py-0.5 text-[10px] bg-accent text-bg font-mono rounded hover:bg-accent-bright disabled:opacity-50">
        {saving ? '…' : 'save'}
      </button>
      <button onClick={() => { setOpen(false); setValue(''); setErr(null); }} disabled={saving}
        className="text-text-faint hover:text-text text-[14px] leading-none px-0.5">×</button>
      {err && <span className="text-[10px] text-red font-mono">{err}</span>}
    </div>
  );
}
